import { pseudonymFor } from "./anonymity.ts";

export type AudienceIdentityMode = "fully_anonymous" | "staff_identified" | "public_name";
export type PublicAuthorDisplay = "identity" | "hidden";
export type AudienceIdentityViewer = "participant" | "projector" | "moderator" | "operator";
export type AudienceAuthorIdentityKind = "name" | "pseudonym" | "hidden" | "ai";

export interface AudienceIdentityPolicy {
  identityMode: AudienceIdentityMode;
  publicAuthorDisplay: PublicAuthorDisplay;
}

export interface AudienceIdentityPolicySource {
  identityMode?: AudienceIdentityMode;
  publicAuthorDisplay?: PublicAuthorDisplay;
  anonymousAudience?: boolean;
}

const PRIVACY_RANK: Record<AudienceIdentityMode, number> = {
  public_name: 0,
  staff_identified: 1,
  fully_anonymous: 2,
};

export function normalizeAudienceIdentityPolicy(
  source: AudienceIdentityPolicySource,
): AudienceIdentityPolicy {
  return {
    identityMode: source.identityMode ?? (source.anonymousAudience ? "staff_identified" : "public_name"),
    publicAuthorDisplay: source.publicAuthorDisplay ?? "identity",
  };
}

export function canIncreaseAudienceIdentityPrivacy(
  current: AudienceIdentityMode,
  next: AudienceIdentityMode,
): boolean {
  return PRIVACY_RANK[next] >= PRIVACY_RANK[current];
}

export function identityModeRequiresAlias(mode: AudienceIdentityMode): boolean {
  return mode !== "fully_anonymous";
}

export function projectAudienceAuthor({
  authorLabel,
  authorKey,
  sessionId,
  threadOrigin,
  viewer,
  policy,
}: {
  authorLabel: string | null | undefined;
  authorKey: string;
  sessionId: string;
  threadOrigin: "participant" | "human" | "ai";
  viewer: AudienceIdentityViewer;
  policy: AudienceIdentityPolicy;
}): { label: string | null; kind: AudienceAuthorIdentityKind } {
  if (threadOrigin === "ai") return { label: "Comodo", kind: "ai" };

  const publicSurface = viewer === "participant" || viewer === "projector";
  if (publicSurface && policy.publicAuthorDisplay === "hidden") {
    return { label: null, kind: "hidden" };
  }

  if (threadOrigin !== "participant") {
    return { label: authorLabel?.trim() || "Staff", kind: "name" };
  }

  const pseudonymRequired = policy.identityMode === "fully_anonymous" ||
    (policy.identityMode === "staff_identified" && publicSurface);
  if (pseudonymRequired) {
    return { label: pseudonymFor(authorKey, sessionId), kind: "pseudonym" };
  }
  return { label: authorLabel?.trim() || pseudonymFor(authorKey, sessionId), kind: "name" };
}

export interface ParticipantIdentity {
  label: string;
  kind: "name" | "pseudonym";
  aliasRequired: boolean;
  aliasEditable: boolean;
  enteredNameVisibleToStaff: boolean;
}

export function projectParticipantIdentity({
  alias,
  participantKey,
  sessionId,
  policy,
}: {
  alias: string | null | undefined;
  participantKey: string;
  sessionId: string;
  policy: AudienceIdentityPolicy;
}): ParticipantIdentity {
  const named = policy.identityMode === "public_name";
  return {
    label: named ? alias?.trim() || pseudonymFor(participantKey, sessionId) : pseudonymFor(participantKey, sessionId),
    kind: named ? "name" : "pseudonym",
    aliasRequired: identityModeRequiresAlias(policy.identityMode),
    aliasEditable: identityModeRequiresAlias(policy.identityMode),
    enteredNameVisibleToStaff: policy.identityMode !== "fully_anonymous",
  };
}
